import { Button, Col, Form, Row } from 'react-bootstrap';
import InputPriceRate from 'components/others/InputPriceRate';
import { Field } from 'formik';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { safeFloatParse, safeIntParse } from 'utils/helpers/safeParsing';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FilesCloudUpload } from 'modules/files';
import type { Dispatch, SetStateAction } from 'react';
import SolarIcon from 'components/icons/SolarIcon';

interface AddComingFormProps {
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    setFieldValue: (field: string, value: unknown) => void;
    values: Record<string, any>;
    onCancel?: () => void;
    pendingCloudFiles: File[];
    setPendingCloudFiles: Dispatch<SetStateAction<File[]>>;
    cloudProgress: number | boolean;
}

const AddComingForm = ({
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    onCancel,
    pendingCloudFiles,
    setPendingCloudFiles,
    cloudProgress,
}: AddComingFormProps) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const canCredit = Boolean(params.creditControl);
    const ignoreWarehousePrice = Boolean(params.ignoreWarehousePrice);
    const rate = localSettings?.RATE?.current || 1;
    const formDisabled = isSubmitting || cloudProgress !== false;

    return (
        <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('labelCount')}</label>
                <input
                    className="form-control"
                    required
                    type="number"
                    id="inputCount"
                    step={1}
                    value={values?.cCount || ''}
                    disabled={formDisabled}
                    onChange={(e) => setFieldValue('cCount', safeIntParse(e.target.value))}
                    name="comingCount"
                />
            </Form.Group>
            {ignoreWarehousePrice ? (
                <p className="small text-muted mb-3">
                    <Trans
                        i18nKey="messIgnorePriceComingHint"
                        ns="Warehouses"
                        components={{
                            settingsLink: (
                                <Link
                                    to="/settings?activeTab=warehouse"
                                    className="link-primary"
                                />
                            ),
                        }}
                    />
                </p>
            ) : (
                <Row>
                    <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('labelPrice')}</label>
                        <InputPriceRate
                            required={true}
                            value={values?.price}
                            disabled={formDisabled}
                            onValueChange={(val) => {
                                setFieldValue('price', safeFloatParse(val?.value || 0));
                                setFieldValue('priceRate', safeFloatParse(rate * (val?.value || 0)));
                            }}
                        />
                    </Form.Group>
                    </Col>
                    <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('labelPriceSale')}</label>
                        <InputPriceRate
                            required={true}
                            value={values?.priceSale}
                            disabled={formDisabled}
                            onValueChange={(val) => {
                                setFieldValue('priceSale', safeFloatParse(val?.value || 0));
                                setFieldValue('priceSaleRate', safeFloatParse(rate * (val?.value || 0)));
                            }}
                        />
                    </Form.Group>
                    </Col>
                </Row>
            )}
            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('labelDescription')}</label>
                <Field
                    disabled={formDisabled}
                    name="cDescription"
                    as="textarea"
                    rows="4"
                    className="form-control"
                />
            </Form.Group>
            {canCredit ? (
                <>
                    <div className="form-check form-switch mb-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            role="switch"
                            id="ex-switch-payment"
                            checked={Boolean(values?.payment)}
                            disabled={formDisabled}
                            onChange={(e) => {
                                setFieldValue('payment', e.target.checked);
                                if (!e.target.checked) setPendingCloudFiles([]);
                            }}
                        />
                        <label htmlFor="ex-switch-payment" className="form-check-label">
                            {t('labelPayment')}
                        </label>
                    </div>

                    {values?.payment && (
                        <div>
                            <FilesCloudUpload
                                disabled={formDisabled}
                                progress={cloudProgress}
                                files={pendingCloudFiles}
                                setFiles={setPendingCloudFiles}
                                label={t('common:labelAttachments')}
                                buttonLabel={t('common:buttons.upload')}
                                uploadingLabel={t('common:buttons.uploading')}
                            />

                            <Form.Group className="mb-3">
                                <label className="form-label text-muted">{t('labelPaymentDescription')}</label>
                                <Field
                                    disabled={formDisabled}
                                    name="paymentDescription"
                                    as="textarea"
                                    rows="4"
                                    className="form-control"
                                />
                            </Form.Group>
                        </div>
                    )}
                </>
            ) : null}

            <div className="d-flex justify-content-end gap-2">
                <Button size="sm" variant="light" type="button" disabled={formDisabled} onClick={() => onCancel?.()}>
                    {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button size="sm" variant="primary" type="submit" disabled={formDisabled}>
                    <SolarIcon name="plus-lg" className="me-1 fs-14" />
                    {t('btnAdd')}
                </Button>
            </div>
        </Form>
    );
};

export default AddComingForm;
